/***************************************************************************************
 * FileName      : PlaybackEngine.ts
 * Description   : 시나리오 플레이백 엔진 - 시간에 따라 controls/sensors 값을 업데이트
 * Author        : (주)한국공학기술연구원 박은정
 * Created Date  : 2026.02.11
 * Modified Date : 2026.02.13 initialState에 없는 센서/제어 키도 타임라인에서 동적 추가되도록 수정
 * Reference     : doc/scjeil-mtpt-app/계획/01-시나리오 플레이백 구조.md
 ***************************************************************************************/

// 2026.02.11 : 박은정 : 시나리오 타입 임포트
import { ScenarioFile, TimelineEvent, PlaybackStatus, PlaybackSpeed } from '../types/scenario';

// 2026.02.11 : 박은정 : 현재 상태 타입
export interface PlaybackState {
  controls: Record<string, boolean | number>;
  sensors: Record<string, number>;
}

// 2026.02.11 : 박은정 : 플레이백 엔진 클래스
export class PlaybackEngine {
  private scenario: ScenarioFile | null = null;
  private currentTime: number = 0;
  private status: PlaybackStatus = 'stopped';
  private speed: PlaybackSpeed = 1;
  private state: PlaybackState = { controls: {}, sensors: {} };
  private intervalId: NodeJS.Timeout | null = null;
  private timelineEvents: TimelineEvent[] = [];
  private onStateUpdateCallback: ((state: PlaybackState) => void) | null = null;
  private onTimeUpdateCallback: ((time: number) => void) | null = null;
  private onStatusUpdateCallback: ((status: PlaybackStatus) => void) | null = null;

  // 2026.02.11 : 박은정 : 시나리오 로드 및 초기 상태 적용
  public load(scenario: ScenarioFile): void {
    this.scenario = scenario;
    this.currentTime = 0;
    this.status = 'stopped';
    
    // 초기 상태 적용
    this.state = {
      controls: { ...scenario.initialState.controls },
      sensors: { ...scenario.initialState.sensors },
    };

    // 타임라인 이벤트 추출 (트랙 표시용)
    this.timelineEvents = this.extractTimelineEvents(scenario);

    // 초기 상태 콜백 호출
    if (this.onStateUpdateCallback) {
      this.onStateUpdateCallback(this.state);
    }
    if (this.onTimeUpdateCallback) {
      this.onTimeUpdateCallback(this.currentTime);
    }
    if (this.onStatusUpdateCallback) {
      this.onStatusUpdateCallback(this.status);
    }
  }

  // 2026.02.11 : 박은정 : 재생 시작
  // 2026.05.14 : 박은정 : 시나리오 끝까지 재생된 상태에서 다시 재생 누르면 처음부터 재생
  public play(): void {
    if (!this.scenario || this.status === 'playing') return;

    // 끝에 도달한 상태라면 currentTime + state 를 초기 상태로 리셋
    if (this.currentTime >= this.scenario.meta.totalDuration) {
      this.currentTime = 0;
      this.state = {
        controls: { ...this.scenario.initialState.controls },
        sensors: { ...this.scenario.initialState.sensors },
      };
      if (this.onStateUpdateCallback) this.onStateUpdateCallback(this.state);
      if (this.onTimeUpdateCallback) this.onTimeUpdateCallback(this.currentTime);
    }

    this.status = 'playing';
    if (this.onStatusUpdateCallback) {
      this.onStatusUpdateCallback(this.status);
    }

    // 1초마다 업데이트 (재생 속도 적용)
    const intervalMs = 1000 / this.speed;
    this.intervalId = setInterval(() => {
      this.tick();
    }, intervalMs);
  }

  // 2026.02.11 : 박은정 : 일시 정지
  public pause(): void {
    if (this.status !== 'playing') return;

    this.status = 'paused';
    if (this.onStatusUpdateCallback) {
      this.onStatusUpdateCallback(this.status);
    }

    if (this.intervalId) {
      clearInterval(this.intervalId);
      this.intervalId = null;
    }
  }

  // 2026.02.11 : 박은정 : 정지 및 초기 상태로 복귀
  public stop(): void {
    this.pause();
    
    this.status = 'stopped';
    this.currentTime = 0;

    if (this.scenario) {
      // 초기 상태로 복귀
      this.state = {
        controls: { ...this.scenario.initialState.controls },
        sensors: { ...this.scenario.initialState.sensors },
      };
    }

    if (this.onStateUpdateCallback) {
      this.onStateUpdateCallback(this.state);
    }
    if (this.onTimeUpdateCallback) {
      this.onTimeUpdateCallback(this.currentTime);
    }
    if (this.onStatusUpdateCallback) {
      this.onStatusUpdateCallback(this.status);
    }
  }

  // 2026.02.11 : 박은정 : 특정 시점으로 이동
  public seekTo(time: number): void {
    if (!this.scenario) return;

    const clampedTime = Math.max(0, Math.min(time, this.scenario.meta.totalDuration));
    this.currentTime = clampedTime;

    // 해당 시점의 상태 계산
    this.updateStateAtTime(clampedTime);

    if (this.onStateUpdateCallback) {
      this.onStateUpdateCallback(this.state);
    }
    if (this.onTimeUpdateCallback) {
      this.onTimeUpdateCallback(this.currentTime);
    }
  }

  // 2026.02.11 : 박은정 : 재생 속도 설정
  public setSpeed(speed: PlaybackSpeed): void {
    const wasPlaying = this.status === 'playing';
    
    if (wasPlaying) {
      this.pause();
    }

    this.speed = speed;

    if (wasPlaying) {
      this.play();
    }
  }

  // 2026.02.11 : 박은정 : 현재 상태 반환
  public getCurrentState(): PlaybackState {
    return { ...this.state };
  }

  // 2026.02.11 : 박은정 : 현재 시간 반환
  public getCurrentTime(): number {
    return this.currentTime;
  }

  // 2026.02.11 : 박은정 : 현재 상태 반환
  public getStatus(): PlaybackStatus {
    return this.status;
  }

  // 2026.02.11 : 박은정 : 재생 속도 반환
  public getSpeed(): PlaybackSpeed {
    return this.speed;
  }

  // 2026.02.11 : 박은정 : 특정 시점의 활성 phase 반환
  public getActivePhase(time: number): { phaseId: string; name: string } | null {
    if (!this.scenario) return null;

    for (const phase of this.scenario.phases) {
      if (time >= phase.startTime && time <= phase.endTime) {
        return { phaseId: phase.phaseId, name: phase.name };
      }
    }

    return null;
  }

  // 2026.02.11 : 박은정 : 타임라인 이벤트 반환 (트랙 표시용)
  public getTimelineEvents(): TimelineEvent[] {
    return [...this.timelineEvents];
  }

  // 2026.02.11 : 박은정 : 상태 업데이트 콜백 설정
  public onStateUpdate(callback: (state: PlaybackState) => void): void {
    this.onStateUpdateCallback = callback;
  }

  // 2026.02.11 : 박은정 : 시간 업데이트 콜백 설정
  public onTimeUpdate(callback: (time: number) => void): void {
    this.onTimeUpdateCallback = callback;
  }

  // 2026.02.11 : 박은정 : 상태 업데이트 콜백 설정
  public onStatusUpdate(callback: (status: PlaybackStatus) => void): void {
    this.onStatusUpdateCallback = callback;
  }

  // 2026.02.11 : 박은정 : 리소스 정리
  public dispose(): void {
    this.stop();
    this.onStateUpdateCallback = null;
    this.onTimeUpdateCallback = null;
    this.onStatusUpdateCallback = null;
  }

  // 2026.02.11 : 박은정 : 재생 루프 (매 초 호출)
  private tick(): void {
    if (!this.scenario) return;

    this.currentTime += 1;

    // 시나리오 끝 도달 체크
    if (this.currentTime >= this.scenario.meta.totalDuration) {
      this.currentTime = this.scenario.meta.totalDuration;
      this.pause();
    }

    // 현재 시점의 상태 업데이트
    this.updateStateAtTime(this.currentTime);

    if (this.onStateUpdateCallback) {
      this.onStateUpdateCallback(this.state);
    }
    if (this.onTimeUpdateCallback) {
      this.onTimeUpdateCallback(this.currentTime);
    }
  }

  // 2026.02.11 : 박은정 : 특정 시점의 상태 계산
  private updateStateAtTime(time: number): void {
    if (!this.scenario) return;

    // 초기 상태로 시작
    this.state = {
      controls: { ...this.scenario.initialState.controls },
      sensors: { ...this.scenario.initialState.sensors },
    };

    // 2026.02.11 : 박은정 : 모든 phase의 timeline을 순회하며 해당 시점까지의 변경사항 적용
    for (const phase of this.scenario.phases) {
      if (time < phase.startTime) break; // 아직 시작되지 않은 phase는 건너뛰기

      // 2026.02.11 : 박은정 : phase가 끝난 경우, phase의 마지막 값만 적용
      const phaseRelativeTime = Math.min(
        time - phase.startTime,
        phase.endTime - phase.startTime
      );
      
      // 해당 phase의 timeline에서 phaseRelativeTime 이하의 모든 항목 적용
      for (const item of phase.timeline) {
        if (item.t <= phaseRelativeTime) {
          // set의 모든 값을 state에 적용
          for (const [key, value] of Object.entries(item.set)) {
            // key가 controls에 있는지 sensors에 있는지 판별
            if (key in this.scenario.initialState.controls) {
              this.state.controls[key] = value as boolean | number;
            } else if (key in this.scenario.initialState.sensors) {
              this.state.sensors[key] = value as number;
            } else {
              // 2026.02.13 : 박은정 : initialState에 없는 키도 타입 추론으로 동적 추가
              // boolean → controls, number → sensors (타임라인에서 새로 등장하는 키 지원)
              if (typeof value === 'boolean') {
                this.state.controls[key] = value;
              } else {
                this.state.sensors[key] = value as number;
              }
            }
          }
        }
      }
    }
  }

  // 2026.02.11 : 박은정 : 타임라인 이벤트 추출 (트랙 표시용)
  private extractTimelineEvents(scenario: ScenarioFile): TimelineEvent[] {
    const events: TimelineEvent[] = [];

    for (const phase of scenario.phases) {
      for (const item of phase.timeline) {
        const absoluteTime = phase.startTime + item.t;

        for (const [id, value] of Object.entries(item.set)) {
          // id가 controls에 있는지 sensors에 있는지 판별
          const isControl = id in scenario.initialState.controls;
          const isSensor = id in scenario.initialState.sensors;

          // 2026.02.13 : 박은정 : initialState에 없는 키도 타입 추론으로 이벤트 생성
          let eventType: 'control' | 'sensor';
          if (isControl) {
            eventType = 'control';
          } else if (isSensor) {
            eventType = 'sensor';
          } else {
            // initialState에 없는 키: boolean → control, number → sensor
            eventType = typeof value === 'boolean' ? 'control' : 'sensor';
          }

          events.push({
            time: absoluteTime,
            type: eventType,
            id,
            value,
            phaseId: phase.phaseId,
            phaseName: phase.name,
          });
        }
      }
    }

    // 시간순으로 정렬
    events.sort((a, b) => a.time - b.time);

    return events;
  }
}
